import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useCatalog } from '../lib/queries'
import { cue } from '../lib/sound'
import { useDebouncedValue } from '../lib/use-debounced-value'
import { initialsFromTitle, tintFromString } from '../lib/tint'

/**
 * ⌘K / Ctrl-K search over the catalog.
 *
 * The fastest path from "is there a key for X" to the answer, without
 * touching the filter rail or losing the current view. Arrow keys move,
 * Enter opens, Escape closes.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const navigate = useNavigate()

  /*
   * The keyboard listener is bound once, so it cannot read `open` from state
   * without reading a stale one — hence the toggle below going through the
   * updater. The cue needs to know which way the toggle is about to go, so it
   * reads a ref that tracks the same value.
   */
  const openRef = useRef(false)

  /** Set when the dialog is closing because a game was picked, not dismissed. */
  const chosen = useRef(false)

  const debounced = useDebouncedValue(query, 200)
  const results = useCatalog({ q: debounced.trim() || undefined, sort: 'title' })
  const games = (results.data?.games ?? []).slice(0, 8)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()

        // Reaching for search is going somewhere, so it sounds like opening a
        // game. Closing is left to the dialog's own close event, which is the
        // one path Escape, the backdrop and this shortcut all end up taking.
        if (!openRef.current) cue('opened')

        setOpen((previous) => !previous)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    openRef.current = open

    const dialog = dialogRef.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  // A new result set invalidates the old highlight position.
  useEffect(() => setActive(0), [debounced])

  const choose = (slug: string) => {
    cue('opened')

    // Picking a result closes the dialog on the way out, which would otherwise
    // fire a dismissal over the top of the cue for the game being opened.
    chosen.current = true

    setOpen(false)
    setQuery('')
    navigate({ to: '/game/$slug', params: { slug } })
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => Math.min(index + 1, games.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const game = games[active]
      if (game) choose(game.slug)
    }
  }

  return (
    <dialog
      ref={dialogRef}
      // Escape, the backdrop and the shortcut all arrive here, so one cue
      // covers every way of leaving without picking anything.
      onClose={() => {
        if (chosen.current) chosen.current = false
        else cue('dismissed')

        setOpen(false)
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) setOpen(false)
      }}
      className="pop-in mx-auto mt-[12vh] w-[min(34rem,calc(100vw-2rem))] rounded-xl border border-line bg-surface-1 p-0 text-ink backdrop:bg-black/70 backdrop:backdrop-blur-sm"
    >
      <div className="border-b border-line p-3">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
          placeholder="Search games…"
          aria-label="Search games"
          className="w-full bg-transparent px-2 py-1.5 text-base outline-none placeholder:text-ink-muted"
        />
      </div>

      {debounced.trim().length === 0 ? (
        <p className="px-5 py-6 text-sm text-ink-muted">Type to search the library.</p>
      ) : games.length === 0 ? (
        <p className="px-5 py-6 text-sm text-ink-soft">Nothing matches “{debounced}”.</p>
      ) : (
        <ul className="max-h-[50vh] overflow-y-auto p-2">
          {games.map((game, index) => (
            <li key={game.id}>
              <button
                type="button"
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(game.slug)}
                aria-label={`${game.title} — ${game.availableKeys} available`}
                className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${
                  index === active ? 'bg-surface-3' : 'hover:bg-surface-2'
                }`}
              >
                <span
                  className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded"
                  style={{ backgroundColor: tintFromString(game.title) }}
                >
                  {game.capsuleUrl ? (
                    <img src={game.capsuleUrl} alt="" className="size-full object-cover" />
                  ) : (
                    <span className="font-display text-[10px] font-semibold text-white/40">
                      {initialsFromTitle(game.title)}
                    </span>
                  )}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">{game.title}</span>
                <span className="font-mono text-[11px] text-ink-muted tabular-nums">
                  {game.availableKeys}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="border-t border-line px-4 py-2 font-mono text-[10px] text-ink-muted">
        ↑↓ move · ↵ open · esc close
      </p>
    </dialog>
  )
}
